import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Global from '../../../store/global-selectors';
import { useFedexHalOrderSelectedDisabled } from '../disabled-hooks/use-fedex-hal-order-selected-disabled';
import { useOneOrderSelectedDisabled } from '../disabled-hooks/use-one-order-selected-disabled';
import { ComponentStates } from '../../../enums/component-states';
import { getActualSalesOrderIds } from '../../../store/orders/orders.helpers';
import { useOtherActionsContext } from '../../../pages/orders-page/containers/grid-header-container/other-actions-context-provider';
import { disableForItemCountOneOrLess } from '../../../orders/split-ship/disable-split-ship';
import { useContainsDemoOrder } from '../../../demos/hooks/use-contains-demo-order';
import { useCombineSplitOrdersShipmentsFeatureGate } from '../../../feature-gates/use-combine-split-orders-shipments-feature-gate';
import { messages } from './messages';

const getDisabledInfo = ({
  featureGate,
  disabled,
  disabledMessage,
  oneOrderSelectedDisabled,
  isSplitShipRestricted,
  selectedFulfillmentPlans,
  hasDemoOrder,
}) => {
  let isDisabled = false;
  let disabledReasonMessageDescriptor = null;

  if (!featureGate.isFeatureEnabled) {
    isDisabled = true;
    disabledReasonMessageDescriptor = featureGate.disabledMessage;
  } else if (disabled) {
    isDisabled = true;
    disabledReasonMessageDescriptor = disabledMessage;
  } else if (isSplitShipRestricted) {
    isDisabled = true;
    disabledReasonMessageDescriptor = messages.splitShipRestrictedReason;
  } else if (oneOrderSelectedDisabled) {
    isDisabled = true;
    disabledReasonMessageDescriptor = messages.splitShipNotAllowedForMultipleOrders;
  } else if (hasDemoOrder) {
    isDisabled = true;
    disabledReasonMessageDescriptor = messages.isDemoDisabledMessage;
  } else {
    const evaluateItemCount = disableForItemCountOneOrLess(selectedFulfillmentPlans);
    if (evaluateItemCount) {
      isDisabled = true;
      disabledReasonMessageDescriptor = messages.splitShipNotAllowedForOneItemOrders;
    }
  }

  return {
    isDisabled,
    disabledReasonMessageDescriptor,
  };
};

export const useSplitOrder = () => {
  const selectedOrdersIds = useSelector(Global.selectSelectedOrdersIds);
  const { disabled, message } = useFedexHalOrderSelectedDisabled();
  const oneOrderSelectedDisabled = useOneOrderSelectedDisabled();
  const hasDemoOrder = useContainsDemoOrder(selectedOrdersIds);
  const actualSelectedOrdersIds = useSelector((state) => getActualSalesOrderIds(state, selectedOrdersIds));
  const selectedFulfillmentPlans = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
      salesOrderIds: actualSelectedOrdersIds,
    }),
  );
  const restrictions = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId: actualSelectedOrdersIds[0],
      property: 'restrictions',
    }),
  );
  const isSplitShipRestricted =
    actualSelectedOrdersIds.length === 1 ? restrictions?.splitShip?.componentState === ComponentStates.Disabled : false;
  const featureGate = useCombineSplitOrdersShipmentsFeatureGate();

  const { openSplitShipModal } = useOtherActionsContext();

  const actionHandler = useCallback(() => {
    const salesOrderId = actualSelectedOrdersIds[0];
    openSplitShipModal(salesOrderId);
  }, [actualSelectedOrdersIds, openSplitShipModal]);

  const { isDisabled, disabledReasonMessageDescriptor } = getDisabledInfo({
    featureGate,
    disabled,
    disabledMessage: message,
    oneOrderSelectedDisabled,
    isSplitShipRestricted,
    selectedFulfillmentPlans,
    hasDemoOrder,
  });

  return useMemo(() => {
    return {
      actionHandler,
      nameDescriptor: messages.orderGridOtherActionsSplit,
      isDisabled,
      disabledReason: disabledReasonMessageDescriptor,
    };
  }, [actionHandler, disabledReasonMessageDescriptor, isDisabled]);
};
